import { useCallback, useEffect, useRef, useState } from 'react';
import { ClampRatio, DEFAULT_SPLIT_RATIO } from './splitSettings';
import { useTokens } from './tokens';

/**
 * Linha divisória arrastável entre tabela e painel de detalhe.
 *
 * Props:
 *  - orientation: 'vertical' (separa esquerda/direita, drag horizontal, col-resize)
 *                 'horizontal' (separa cima/baixo, drag vertical, row-resize)
 *  - ratio: percentual ATUAL da tabela (0-100). Source of truth no pai.
 *  - onChange(newRatio): chamado durante o drag (sem throttle — é leve).
 *  - containerRef: ref do container que envolve tabela+splitter+detail. Usado pra
 *    calcular o ratio a partir do mouse position.
 */
export function Splitter({ orientation, ratio, onChange, containerRef }) {
    const T = useTokens();
    const [dragging, setDragging] = useState(false);
    const ratioRef = useRef(ratio);
    ratioRef.current = ratio;

    const handleMouseDown = useCallback((e) => {
        e.preventDefault();
        setDragging(true);
    }, []);

    // Duplo clique reseta pro padrão do projeto. Aproveita o handler de change
    // do hook useSplitRatio que persiste em localStorage automaticamente.
    const handleDoubleClick = useCallback(
        (e) => {
            e.preventDefault();
            onChange(DEFAULT_SPLIT_RATIO);
        },
        [onChange],
    );

    useEffect(() => {
        if (!dragging) {
            return undefined;
        }

        const handleMove = (e) => {
            const container = containerRef.current;
            if (!container) {
                return;
            }
            const rect = container.getBoundingClientRect();
            const pct =
                orientation === 'vertical'
                    ? ((e.clientX - rect.left) / rect.width) * 100
                    : ((e.clientY - rect.top) / rect.height) * 100;
            const next = ClampRatio(pct);
            if (next !== ratioRef.current) {
                onChange(next);
            }
        };

        const handleUp = () => setDragging(false);

        document.addEventListener('mousemove', handleMove);
        document.addEventListener('mouseup', handleUp);
        // Trava cursor + seleção durante o drag.
        const prevCursor = document.body.style.cursor;
        const prevSelect = document.body.style.userSelect;
        document.body.style.cursor = orientation === 'vertical' ? 'col-resize' : 'row-resize';
        document.body.style.userSelect = 'none';

        return () => {
            document.removeEventListener('mousemove', handleMove);
            document.removeEventListener('mouseup', handleUp);
            document.body.style.cursor = prevCursor;
            document.body.style.userSelect = prevSelect;
        };
    }, [dragging, orientation, onChange, containerRef]);

    const isVertical = orientation === 'vertical';
    return (
        <div
            role="separator"
            aria-orientation={isVertical ? 'vertical' : 'horizontal'}
            aria-valuenow={Math.round(ratio)}
            tabIndex={-1}
            onMouseDown={handleMouseDown}
            onDoubleClick={handleDoubleClick}
            title="Arraste para redimensionar · duplo clique restaura 50/50"
            style={{
                flex: '0 0 6px',
                background: dragging ? T.accent : T.line,
                cursor: isVertical ? 'col-resize' : 'row-resize',
                transition: dragging ? 'none' : 'background 0.15s',
                position: 'relative',
                zIndex: 1,
                ...(isVertical ? { height: '100%', width: 6 } : { width: '100%', height: 6 }),
            }}
        >
            {/* Hit-area expandida (12px) sobre o visível de 6px pra facilitar pega */}
            <div
                aria-hidden="true"
                style={{
                    position: 'absolute',
                    ...(isVertical
                        ? { top: 0, bottom: 0, left: -3, right: -3 }
                        : { left: 0, right: 0, top: -3, bottom: -3 }),
                }}
            />
        </div>
    );
}
